import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import AppButton from '../AppButton';
import { QuestLabel, MarkdownDisplay, ResourceLinks, PageShell } from '../shared';
import { getLocalizedText } from '../../utils/localizationHelpers';

export default function QuestDetailView({
  quest,
  onBack,
  onStartQuest,
  hasActiveQuest = false,
  pendingRequests = []
}) {
  const { t } = useTranslation();
  const [descExpanded, setDescExpanded] = useState(false);

  if (!quest) return null;

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo':
      case 'image':
        return ICONS.IMAGE || ICONS.CAMERA || ICONS.FILE;
      case 'pdf':
        return ICONS.FILE_PDF || ICONS.FILE;
      case 'excel':
        return ICONS.FILE_EXCEL || ICONS.FILE;
      default:
        return ICONS.LINK || ICONS.EXTERNAL_LINK;
    }
  };

  const handleStartQuest = () => {
    if (hasActiveQuest || pendingRequests.length > 0) return;
    onStartQuest(quest.id);
    onBack();
  };

  const canStartQuest = !hasActiveQuest && pendingRequests.length === 0;
  const disabledReason = hasActiveQuest
    ? 'You have an active quest. Complete it first.'
    : pendingRequests.length > 0
    ? 'You have a pending quest request. Wait for approval.'
    : '';

  const totalXP = (quest.value || quest.reward || 0) +
    (quest.sideQuests || []).reduce((sum, sq) => sum + (sq.value || 0), 0);

  return (
    <PageShell className="qp-page qdv-page">
      {/* Back button */}
      <div className="qdv-top-bar">
        <button className="qdv-back-btn" onClick={onBack}>
          <FontAwesomeIcon icon={ICONS.CHEVRON_LEFT} />
          <span>Available Quests</span>
        </button>
      </div>

      {/* Hero with action button */}
      <div className="qdv-hero">
        {quest.icon && <div className="qdv-hero-bg" style={{ backgroundImage: `url(${quest.icon})` }} />}
        <div className="qdv-hero-gradient" />
        <div className="qdv-hero-content">
          {quest.icon && (
            <div className="qdv-icon">
              <img src={quest.icon} alt="Quest icon" />
            </div>
          )}
          <div className="qdv-hero-info">
            <h2 className="qdv-title">{getLocalizedText(quest.title)}</h2>
            <div className="qdv-badges">
              <QuestLabel type="xp" value={totalXP} />
              <QuestLabel type="difficulty" value={quest.difficulty} />
              {quest.timeEstimate && <QuestLabel type="time" value={quest.timeEstimate} />}
              {quest.sideQuests?.length > 0 && <QuestLabel type="sideQuests" value={quest.sideQuests.length} />}
            </div>
          </div>
          {onStartQuest && (
            <div className="qdv-hero-action">
              <AppButton
                className="qdv-start-btn"
                onClick={handleStartQuest}
                icon={ICONS.PLAY}
                disabled={!canStartQuest}
                title={disabledReason}
              >
                {t('quests.requestApproval')}
              </AppButton>
              {!canStartQuest && (
                <div className="qdv-warning">
                  <FontAwesomeIcon icon={ICONS.EXCLAMATION_TRIANGLE || ICONS.WARNING} className="qdv-warning-icon" />
                  <span className="qdv-warning-text">{disabledReason}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="qdv-body">
        <ResourceLinks missionBriefUrl={quest.missionBriefUrl} pressKitUrl={quest.pressKitUrl} />

        {/* Collapsible Description */}
        <div className="qdm-description">
          <button className="qdv-section-toggle" onClick={() => setDescExpanded(v => !v)}>
            <FontAwesomeIcon
              icon={ICONS.CHEVRON_DOWN}
              className={`cq-toggle-chevron ${descExpanded ? 'expanded' : ''}`}
            />
            <h4>{t('quests.description')}</h4>
          </button>
          <div className={`cq-objective-desc-wrap ${descExpanded ? 'expanded' : ''}`}>
            <div className="cq-objective-desc-overflow">
              <div className="qdm-description-content">
                <MarkdownDisplay content={getLocalizedText(quest.description)} />
              </div>
            </div>
          </div>
        </div>

        {/* Proof Requirements */}
        {quest.proofRequirements?.length > 0 && (
          <div className="qdm-requirements">
            <div className="qdm-req-header">
              <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} className="qdm-req-icon" />
              <span>Proof Requirements ({quest.proofRequirements.length})</span>
            </div>
            <div className="qdm-req-list">
              {quest.proofRequirements.map((proof, reqIndex) => {
                const inputType = (proof.inputType || 'link').toLowerCase();
                return (
                  <div key={reqIndex} className="qdm-req-item">
                    <div className="qdm-req-type">
                      <FontAwesomeIcon icon={getTypeIcon(inputType)} />
                      <span>{inputType.toUpperCase()}</span>
                    </div>
                    <span className="qdm-req-label">
                      {getLocalizedText(proof.label, `Proof ${reqIndex + 1}`)}
                      {proof.required && <span className="qdm-required">*</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Side Quests */}
        {quest.sideQuests?.length > 0 && (
          <div className="qdm-side-quests">
            <h4>
              <FontAwesomeIcon icon={ICONS.LIST} />
              Side Quests ({quest.sideQuests.length})
            </h4>
            <div className="qdm-sq-list">
              {quest.sideQuests.map((sideQuest, index) => (
                <div key={index} className="qdm-sq-item" data-difficulty={sideQuest.difficulty}>
                  <div className="qdm-sq-header">
                    <span className="qdm-sq-title">{getLocalizedText(sideQuest.title)}</span>
                    <div className="qdm-sq-badges">
                      <QuestLabel type="difficulty" value={sideQuest.difficulty} size="small" />
                      <QuestLabel type="xp" value={sideQuest.value} size="small" color="neutral" />
                    </div>
                  </div>
                  {sideQuest.description && (
                    <div className="qdm-sq-description">
                      <MarkdownDisplay content={getLocalizedText(sideQuest.description)} />
                    </div>
                  )}
                  {sideQuest.proofRequirements?.length > 0 && (
                    <div className="qdm-requirements qdm-requirements-nested">
                      <div className="qdm-req-header">
                        <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} className="qdm-req-icon" />
                        <span>Proof Requirements ({sideQuest.proofRequirements.length})</span>
                      </div>
                      <div className="qdm-req-list">
                        {sideQuest.proofRequirements.map((proof, reqIndex) => {
                          const inputType = (proof.inputType || 'link').toLowerCase();
                          return (
                            <div key={reqIndex} className="qdm-req-item">
                              <div className="qdm-req-type">
                                <FontAwesomeIcon icon={getTypeIcon(inputType)} />
                                <span>{inputType.toUpperCase()}</span>
                              </div>
                              <span className="qdm-req-label">
                                {getLocalizedText(proof.label, `Proof ${reqIndex + 1}`)}
                                {proof.required && <span className="qdm-required">*</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
